"use client";

import type { DataUIPart } from "ai";
import type React from "react";
import { createContext, useContext, useMemo, useState } from "react";
import type { CustomUIDataTypes } from "@/lib/types";

/**
 * Context for stable setter functions that never change.
 * Components using only these don't re-render when stream data updates.
 */
type DataStreamActionsContextValue = {
  setDataStream: React.Dispatch<
    React.SetStateAction<DataUIPart<CustomUIDataTypes>[]>
  >;
  setStreamingChatId: React.Dispatch<React.SetStateAction<string | null>>;
};

/**
 * Context for reactive stream state that triggers re-renders.
 * Only components that actually need to react to streaming updates should use this.
 */
type DataStreamStateContextValue = {
  dataStream: DataUIPart<CustomUIDataTypes>[];
  streamingChatId: string | null;
};

const DataStreamActionsContext = createContext<DataStreamActionsContextValue | null>(null);
const DataStreamStateContext = createContext<DataStreamStateContextValue | null>(null);
const StreamingChatIdContext = createContext<string | null>(null);

export function DataStreamProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [dataStream, setDataStream] = useState<DataUIPart<CustomUIDataTypes>[]>([]);
  const [streamingChatId, setStreamingChatId] = useState<string | null>(null);

  // Memoize actions - they never change, so components using only these won't re-render
  const actions = useMemo(() => ({ setDataStream, setStreamingChatId }), []);

  // Memoize state - components using this will re-render when dataStream or streamingChatId changes
  const state = useMemo(() => ({ dataStream, streamingChatId }), [dataStream, streamingChatId]);

  return (
    <DataStreamActionsContext.Provider value={actions}>
      <DataStreamStateContext.Provider value={state}>
        <StreamingChatIdContext.Provider value={streamingChatId}>
          {children}
        </StreamingChatIdContext.Provider>
      </DataStreamStateContext.Provider>
    </DataStreamActionsContext.Provider>
  );
}

/**
 * Hook to get stable setter functions for data stream.
 * Components using ONLY this hook will NOT re-render when stream data updates.
 * Use this for components that need to write to the stream but don't need to read it.
 */
export function useDataStreamActions() {
  const context = useContext(DataStreamActionsContext);
  if (!context) {
    throw new Error("useDataStreamActions must be used within a DataStreamProvider");
  }
  return context;
}

/**
 * Hook to get reactive stream state.
 * Components using this hook WILL re-render when dataStream or streamingChatId changes.
 * Use this only for components that actually need to react to streaming updates.
 */
export function useDataStreamState() {
  const context = useContext(DataStreamStateContext);
  if (!context) {
    throw new Error("useDataStreamState must be used within a DataStreamProvider");
  }
  return context;
}

/**
 * Hook to get ONLY the streamingChatId.
 * Components using this hook will ONLY re-render when streamingChatId changes,
 * NOT when dataStream chunks arrive. Use this for sidebar and navigation components.
 */
export function useStreamingChatId() {
  const context = useContext(StreamingChatIdContext);
  if (context === undefined) {
    throw new Error("useStreamingChatId must be used within a DataStreamProvider");
  }
  return context;
}
